import React, { useState, useEffect, useRef } from 'react';
import { formatMontant, formatDate } from '../../utils/format';
import { Printer } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/common/Input';
import { useAuth } from '../../context/AuthContext';
import { SelecteurFormat } from '../../components/common/DocumentImprimable';
import useFormatImpression from '../../components/common/useFormatImpression';

export default function BonSortie({ sortieId, onClose }) {
  const { nomCommerce } = useAuth();
  const nomEntreprise = nomCommerce || 'Mon entreprise';
  const [format, setFormat] = useFormatImpression();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    api.get(`/vendeurs/sorties/${sortieId}/bon`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sortieId]);

  const styleThermique = `
    @page { size: 80mm auto; margin: 2mm 3mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', Courier, monospace; font-size: 10px; color: #000; background: #fff; width: 74mm; max-width: 74mm; padding: 3px 2px; }
    .text-center { text-align: center; }
    .text-2xl { font-size: 12px !important; font-weight: 900; letter-spacing: 0.5px; }
    .text-lg { font-size: 11px !important; font-weight: 700; }
    .text-sm { font-size: 10px !important; } .text-xs { font-size: 9px !important; }
    .font-black { font-weight: 900; } .font-bold { font-weight: 700; } .font-semibold { font-weight: 600; }
    .border-b-2 { border-bottom: 2px solid #000; } .border-t { border-top: 1px solid #ccc; }
    .border-gray-200 { border-color: #ccc; } .border-gray-400 { border-color: #777; }
    .pb-4 { padding-bottom: 5px; } .mb-4 { margin-bottom: 6px; } .mb-2 { margin-bottom: 3px; }
    .mt-2 { margin-top: 3px; } .mt-4, .mt-6 { margin-top: 5px; } .pt-4 { padding-top: 5px; } .my-3 { margin: 4px 0; }
    .p-2, .p-3 { padding: 3px 4px; } .space-y-1 > * + * { margin-top: 2px; }
    .grid.grid-cols-2 { display: block; }
    .border.border-gray-200.rounded.p-2 { border: 1px solid #ccc; padding: 2px 4px; margin-bottom: 2px; }
    .text-gray-500 { color: #555; } .text-gray-400 { color: #777; } .uppercase { text-transform: uppercase; }
    table { width: 100%; border-collapse: collapse; margin: 4px 0; font-size: 9px; }
    th { border: 1px solid #ccc; padding: 2px 3px; text-align: left; font-size: 8px; text-transform: uppercase; }
    td { border: 1px solid #ccc; padding: 2px 3px; }
    .bg-gray-100 { background: #eee; } .bg-gray-50 { background: #f9f9f9; }
    .bg-black.text-white.p-3.rounded.text-center.my-3 { background: #000; color: #fff; padding: 4px; text-align: center; margin: 4px 0; }
    .text-2xl.font-black { font-size: 14px !important; font-weight: 900; }
    img { display: block; margin: 0 auto 3px; width: 36px; height: 36px; object-fit: contain; }
    .grid.grid-cols-2.gap-8 { display: block; }
    .text-center.h-12 { height: 18px; border-bottom: 1px solid #000; margin-bottom: 2px; display: block; }
    .gap-8, .gap-3 { gap: 4px; } .h-12 { height: 18px; }
  `;
  const styleA4 = `
    @page { size: A4 portrait; margin: 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #000; background: #fff; }
    .text-center { text-align: center; }
    .text-2xl { font-size: 22px; font-weight: 900; }
    .text-lg { font-size: 15px; font-weight: 700; }
    .text-sm { font-size: 12px; } .text-xs { font-size: 10px; }
    .font-black { font-weight: 900; } .font-bold { font-weight: 700; } .font-semibold { font-weight: 600; }
    .border-b-2 { border-bottom: 2px solid #000; } .border-t { border-top: 1px solid #e5e7eb; }
    .border-gray-200 { border-color: #e5e7eb; } .border-gray-400 { border-color: #9ca3af; }
    .pb-4 { padding-bottom: 12px; } .mb-4 { margin-bottom: 14px; } .mb-2 { margin-bottom: 6px; }
    .mt-2 { margin-top: 6px; } .mt-4 { margin-top: 14px; } .mt-6 { margin-top: 22px; } .pt-4 { padding-top: 12px; } .my-3 { margin: 14px 0; }
    .p-2 { padding: 6px; } .p-3 { padding: 10px; }
    .grid { display: grid; } .grid-cols-2 { grid-template-columns: 1fr 1fr; } .gap-3 { gap: 10px; } .gap-8 { gap: 30px; }
    .border { border: 1px solid #e5e7eb; } .rounded { border-radius: 6px; }
    .text-gray-500 { color: #6b7280; } .text-gray-400 { color: #9ca3af; } .uppercase { text-transform: uppercase; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 12px; }
    th { border: 1px solid #e5e7eb; padding: 7px 9px; text-align: left; font-size: 11px; text-transform: uppercase; background: #f3f4f6; }
    td { border: 1px solid #e5e7eb; padding: 7px 9px; }
    .bg-gray-100 { background: #f3f4f6; } .bg-gray-50 { background: #f9fafb; }
    .bg-black.text-white.p-3.rounded.text-center.my-3 { background: #000; color: #fff; padding: 12px; text-align: center; margin: 14px 0; border-radius: 6px; }
    .text-2xl.font-black { font-size: 26px; font-weight: 900; }
    img { display: block; margin: 0 auto 8px; width: 64px; height: 64px; object-fit: contain; }
    .h-12 { height: 42px; }
  `;

  const handlePrint = () => {
    const contenu = printRef.current.innerHTML;
    const styleChoisi = format === 'a4' ? styleA4 : styleThermique;
    const w = window.open('', '_blank');
    w.document.write(
      `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Bon de Sortie — ${nomEntreprise}</title>`
      + `<style>${styleChoisi}</style></head><body>${contenu}</body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 250);
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full" /></div>;
  if (!data) return <p className="text-center text-red-400 py-8">Erreur chargement du bon</p>;

  const { sortie, lignes, totalPieces, montantAttendu, retour } = data;
  const estCloture   = !!retour;
  const estCommission = estCloture && Number(retour.commission_totale) > 0
                      || (sortie.type_remuneration === 'commission' && estCloture);
  const ecart        = estCloture ? Number(retour.ecart) || 0 : 0;
  // Écart négatif dont la dette a été entièrement remboursée → on le barre.
  const ecartRegularise = estCloture && ecart < 0
    && (retour.dette_statut === 'soldee' || Number(retour.dette_montant_restant) === 0);
  const totalVendu   = estCloture
    ? lignes.reduce((s, l) => s + (parseInt(l.quantite_vendue) || 0), 0) : 0;
  const totalRetourne = estCloture
    ? lignes.reduce((s, l) => s + (parseInt(l.quantite_retournee) || 0), 0) : 0;

  return (
    <div className="space-y-4">
      {/* Aperçu imprimable */}
      <div ref={printRef} className="bg-white text-black rounded-xl p-6 text-sm">
        {/* Header */}
        <div className="text-center border-b-2 border-black pb-4 mb-4">
          <img src="/logoSV.png" alt={nomEntreprise} style={{width:'64px', height:'64px', objectFit:'contain', margin:'0 auto 6px'}} />
          <h1 className="text-2xl font-black">{nomEntreprise}</h1>
          <h2 className="text-lg font-bold mt-2">
            {estCloture ? 'BON DE CLÔTURE VENDEUR' : 'BON DE SORTIE VENDEUR'}
          </h2>
        </div>

        {/* Infos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Vendeur', value: `${sortie.prenom} ${sortie.nom}` },
            { label: 'Téléphone', value: sortie.telephone || '—' },
            { label: 'Date', value: formatDate(sortie.date) },
            { label: 'Validé par', value: sortie.validateur || '—' },
          ].map(({ label, value }) => (
            <div key={label} className="border border-gray-200 rounded p-2">
              <p className="text-gray-500 text-xs uppercase font-semibold">{label}</p>
              <p className="font-bold text-sm">{value}</p>
            </div>
          ))}
        </div>

        {/* Articles */}
        <div className="table-scroll mb-4 print:overflow-visible">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-2 text-left">Produit</th>
              <th className="border border-gray-300 p-2 text-left">Modèle / Taille / Couleur</th>
              <th className="border border-gray-300 p-2 text-center">{estCloture ? 'Confié' : 'Qté'}</th>
              {estCloture && <th className="border border-gray-300 p-2 text-center">Vendu</th>}
              {estCloture && <th className="border border-gray-300 p-2 text-center">Retourné</th>}
              <th className="border border-gray-300 p-2 text-right">Prix unitaire</th>
              <th className="border border-gray-300 p-2 text-right">Sous-total</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                <td className="border border-gray-300 p-2">{l.produit_nom}</td>
                <td className="border border-gray-300 p-2">{l.modele} {l.taille ? `/ ${l.taille}` : ''} / {l.couleur}</td>
                <td className="border border-gray-300 p-2 text-center font-bold">{l.quantite_confiee}</td>
                {estCloture && <td className="border border-gray-300 p-2 text-center font-bold">{l.quantite_vendue}</td>}
                {estCloture && <td className="border border-gray-300 p-2 text-center">{l.quantite_retournee}</td>}
                <td className="border border-gray-300 p-2 text-right">{formatMontant(l.prix_vente_unitaire)}</td>
                <td className="border border-gray-300 p-2 text-right font-bold">{formatMontant(l.montant_attendu)}</td>
              </tr>
            ))}
            <tr className="bg-gray-100 font-bold">
              <td colSpan="2" className="border border-gray-300 p-2">TOTAL</td>
              <td className="border border-gray-300 p-2 text-center">{totalPieces} pcs</td>
              {estCloture && <td className="border border-gray-300 p-2 text-center">{totalVendu} pcs</td>}
              {estCloture && <td className="border border-gray-300 p-2 text-center">{totalRetourne} pcs</td>}
              <td className="border border-gray-300 p-2"></td>
              <td className="border border-gray-300 p-2 text-right">{formatMontant(montantAttendu)}</td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* Détail des ventes réelles & commissions (mode commission) */}
        {estCloture && estCommission && lignes.some(l => (l.ventes_reelles || []).length > 0) && (
          <div className="mb-4">
            <p className="text-xs uppercase font-semibold text-gray-500 mb-1">Ventes réelles &amp; commissions</p>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-2 text-left">Article</th>
                  <th className="border border-gray-300 p-2 text-center">Qté</th>
                  <th className="border border-gray-300 p-2 text-right">Prix réel</th>
                  <th className="border border-gray-300 p-2 text-right">Commission</th>
                </tr>
              </thead>
              <tbody>
                {lignes.flatMap((l) =>
                  (l.ventes_reelles || []).map((v, j) => (
                    <tr key={`${l.id}-${j}`}>
                      <td className="border border-gray-300 p-2">{l.produit_nom} {l.taille ? `/ ${l.taille}` : ''}</td>
                      <td className="border border-gray-300 p-2 text-center">{v.quantite}</td>
                      <td className="border border-gray-300 p-2 text-right">{formatMontant(v.prix_reel_unitaire)}</td>
                      <td className="border border-gray-300 p-2 text-right">{formatMontant((parseInt(v.quantite) || 0) * (parseFloat(v.commission_unitaire) || 0))}</td>
                    </tr>
                  ))
                )}
                <tr className="bg-gray-100 font-bold">
                  <td colSpan="3" className="border border-gray-300 p-2">COMMISSION TOTALE VERSÉE</td>
                  <td className="border border-gray-300 p-2 text-right">{formatMontant(retour.commission_totale)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Total box — avant clôture : montant à remettre le soir */}
        {!estCloture && (
          <div className="bg-black text-white p-3 rounded text-center my-3">
            <p className="text-xs uppercase">Montant total à remettre ce soir</p>
            <p className="text-2xl font-black">{formatMontant(montantAttendu)}</p>
          </div>
        )}

        {/* Récapitulatif de clôture — après le retour du soir */}
        {estCloture && (
          <div className="my-3">
            <table className="w-full text-xs">
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-2 text-gray-500 uppercase">Valeur vendue</td>
                  <td className="border border-gray-300 p-2 text-right font-bold">{formatMontant(retour.valeur_vendue)}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 text-gray-500 uppercase">Montant remis</td>
                  <td className="border border-gray-300 p-2 text-right font-bold">{formatMontant(retour.montant_remis)}</td>
                </tr>
                {estCommission && (
                  <tr>
                    <td className="border border-gray-300 p-2 text-gray-500 uppercase">Commission versée</td>
                    <td className="border border-gray-300 p-2 text-right font-bold">{formatMontant(retour.commission_totale)}</td>
                  </tr>
                )}
                <tr>
                  <td className="border border-gray-300 p-2 text-gray-500 uppercase">Écart</td>
                  <td className="border border-gray-300 p-2 text-right font-bold">
                    {ecartRegularise ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#777' }}>
                          {formatMontant(ecart)}
                        </span>
                        <span style={{ marginLeft: 6 }}>régularisé</span>
                      </>
                    ) : (
                      <>{ecart > 0 ? '+' : ''}{formatMontant(ecart)}</>
                    )}
                  </td>
                </tr>
                {retour.dette_montant_restant > 0 && (
                  <tr>
                    <td className="border border-gray-300 p-2 text-gray-500 uppercase">Dette restante</td>
                    <td className="border border-gray-300 p-2 text-right font-bold">{formatMontant(retour.dette_montant_restant)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="bg-black text-white p-3 rounded text-center my-3">
              <p className="text-xs uppercase">
                {ecart < 0
                  ? (retour.dette_montant_restant > 0 ? 'Dette restante après régularisation' : 'Écart régularisé — dette soldée')
                  : 'Montant remis'}
              </p>
              <p className="text-2xl font-black">
                {formatMontant(ecart < 0 ? retour.dette_montant_restant : retour.montant_remis)}
              </p>
            </div>

            {retour.explication_ecart && (
              <p className="text-xs text-gray-500 mb-2">
                <span className="uppercase font-semibold">Explication de l'écart : </span>
                {retour.explication_ecart}
              </p>
            )}
          </div>
        )}

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-4 sm:gap-8 mt-6 pt-4 border-t border-gray-200">
          <div className="text-center">
            <div className="h-12 border-b border-gray-400 mb-2"></div>
            <p className="text-xs text-gray-500">Signature du vendeur</p>
            <p className="text-xs font-semibold">{sortie.prenom} {sortie.nom}</p>
          </div>
          <div className="text-center">
            <div className="h-12 border-b border-gray-400 mb-2"></div>
            <p className="text-xs text-gray-500">Signature du responsable</p>
            <p className="text-xs font-semibold">{sortie.validateur || 'Mon entreprise'}</p>
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-4">{nomEntreprise} © {new Date().getFullYear()} — Document généré automatiquement</p>
      </div>

      {/* Choix du format + actions */}
      <div className="flex justify-center print:hidden">
        <SelecteurFormat format={format} onChange={setFormat} />
      </div>
      <div className="flex space-x-3">
        <Button variant="secondary" onClick={onClose} className="flex-1">Fermer</Button>
        <Button onClick={handlePrint} className="flex-1">
          <Printer size={16} /><span>Imprimer le bon</span>
        </Button>
      </div>
    </div>
  );
}
